/* eslint-disable no-nested-ternary */
// @mui material components
import Card from "@mui/material/Card";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Avatar,
  CircularProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import brandWhite from "assets/images/Alysio-logo-white.svg";
import { GenerateRandomBackgroundColor } from "utils/GenerateRandomBackgroundColor";
import { Add } from "@mui/icons-material";
import { getAPICall } from "lib/callApi";
import {
  COMPANY_LIST_FROM_DOMAIN,
  INVITE_SENT,
  PENDING_CONFIRMATION,
  ACTIVE,
  TEAM_FROM_VALID_INVITE,
} from "lib/constants";
import useAuth from "auth/useAuth";
import CoverLayout from "../components/CoverLayout";
import WorkspaceForm from "./WorkspaceForm";

function Workspace() {
  const location = useLocation();
  const locationSearchParams = new URLSearchParams(location.search);
  const navigate = useNavigate();
  const { handleLogout, user } = useAuth();
  const [showWorkSpaceForm, setShowWorkSpaceForm] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [teams, setTeams] = useState([]);
  const [isInvitedUser, setIsInvitedUser] = useState(
    locationSearchParams.get("redirect") === "invited-user"
  );
  const [inviteCode] = useState(locationSearchParams.get("invite_code"));
  const loginUser = useSelector((state) => state.loginUser.loginUserState);
  const domain = loginUser?.email?.split("@")?.[1];

  const fetchCompanyListFromDomain = async () => {
    setIsLoading(true);
    const hasToken = localStorage.getItem("token");
    const result = await getAPICall(`${COMPANY_LIST_FROM_DOMAIN}?domain=${domain}`, !!hasToken);
    if (result?.data?.success) {
      if (!result?.data?.data?.length) {
        setShowWorkSpaceForm(true);
      }
      setTeams(result.data.data || []);
      if (locationSearchParams.get("redirect") === "invited-user") {
        const activeTeam = result?.data?.data?.find(
          (team) => team?.userWorkspaceStatus === "active" && !team?.userDisabled
        );
        if (activeTeam) {
          setSelectedTeam(activeTeam);
          setShowWorkSpaceForm(true);
          setIsInvitedUser(true);
        }
      }
    } else {
      setTeams([]);
    }
    setIsLoading(false);
  };

  const fetchTeamFromInviteCode = async () => {
    if (!domain) return;
    const result = await getAPICall(`${TEAM_FROM_VALID_INVITE}?invite_code=${inviteCode}`, true);

    if (result?.data?.success) {
      setShowWorkSpaceForm(true);
      if (result?.data?.data) {
        setShowWorkSpaceForm(true);
        setSelectedTeam(result.data.data);
      }
    } else {
      navigate("/home");
    }
  };

  useEffect(() => {
    if (!domain) navigate(`/sign-in${location.search || ""}`, { replace: true });
    if (!inviteCode) {
      fetchCompanyListFromDomain();
    } else {
      fetchTeamFromInviteCode();
    }
  }, [domain, isInvitedUser, inviteCode]);

  const isSignedIn = loginUser?.email && loginUser?.emailVerified;

  const redirectUserTo =
    loginUser.type === "admin"
      ? "/registrations"
      : isInvitedUser
      ? `/workspace?redirect=invited-user&invite_code=${inviteCode}`
      : "/home";

  return (
    <>
      <MDBox
        component={NavLink}
        to={redirectUserTo}
        display="flex"
        alignItems="center"
        justifyContent="center"
        sx={{
          position: "absolute",
          height: { xs: "25%", md: "100%" },
          width: { xs: "100%", md: "50%" },
        }}
        onClick={() => {
          if (`${location.pathname}${location?.search || ""}` === redirectUserTo)
            window.location.reload();
        }}
      >
        <MDBox component="img" src={brandWhite} alt="Brand" width="300px" height="300px" />
      </MDBox>
      {showWorkSpaceForm ? (
        <>
          {!!(isSignedIn && user?.disabled) && (
            <MDBox
              sx={{
                right: 0,
              }}
              pt={4}
              px={3}
              position="absolute"
            >
              <MDButton variant="gradient" color="error" size="small" onClick={handleLogout}>
                Sign Out
              </MDButton>
            </MDBox>
          )}
          <WorkspaceForm
            selectedTeam={selectedTeam}
            setShowWorkSpaceForm={setShowWorkSpaceForm}
            setSelectedTeam={setSelectedTeam}
            hasTeams={!!teams?.length}
            isInvitedUser={isInvitedUser}
            inviteCode={inviteCode}
          />
        </>
      ) : (
        !isLoading &&
        teams.length && (
          <CoverLayout
            justifyContent="end"
            pageLayoutStyle={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              background: "black",
            }}
            skipCover
            gridItemStyle={{ minWidth: { xs: "90vw", md: "50vw" } }}
          >
            <MDBox
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <MDBox sx={{ minWidth: { xs: "100%", md: "60%" } }}>
                <Card sx={{ padding: "1rem" }}>
                  {isSignedIn && (
                    <MDBox pt={4} px={3} display="flex" justifyContent="space-between">
                      <MDTypography variant="h6" fontWeight="light">
                        You are signed in.
                      </MDTypography>
                      <MDButton
                        variant="gradient"
                        color="error"
                        size="small"
                        onClick={handleLogout}
                      >
                        Sign Out
                      </MDButton>
                    </MDBox>
                  )}
                  <MDBox pt={4} pb={3} px={3}>
                    <MDTypography variant="h4" fontWeight="medium">
                      Join other team at
                    </MDTypography>
                    <MDTypography variant="h2" fontWeight="medium">
                      @{domain}
                    </MDTypography>
                    <MDTypography variant="body2">
                      You will need to request access to this organization
                    </MDTypography>
                  </MDBox>

                  <List
                    sx={{
                      width: "100%",
                      bgcolor: "background.paper",
                      maxHeight: "45vh",
                      overflow: "auto",
                    }}
                  >
                    {teams.map((team) => {
                      const userWorkspaceStatus = team?.userWorkspaceStatus;
                      const teamButtonData = {
                        label: "Join",
                        disabled: false,
                      };

                      if (team?.userDisabled) {
                        teamButtonData.label = "Disable";
                        teamButtonData.disabled = true;
                      } else {
                        switch (userWorkspaceStatus) {
                          case INVITE_SENT:
                            teamButtonData.label = "Invite Sent";
                            teamButtonData.disabled = true;
                            break;
                          case PENDING_CONFIRMATION:
                            teamButtonData.label = "Request Pending";
                            teamButtonData.disabled = true;
                            break;
                          case ACTIVE:
                            teamButtonData.label = "Member";
                            teamButtonData.disabled = true;
                            break;
                          default:
                            break;
                        }
                      }

                      return (
                        <ListItem
                          key={team.id}
                          alignItems="flex-start"
                          sx={{ padding: ".6rem 1rem" }}
                          secondaryAction={
                            <MDButton
                              variant="gradient"
                              color="success"
                              size="small"
                              sx={{ mr: 2, position: "relative" }}
                              disabled={teamButtonData.disabled}
                              onClick={() => {
                                setSelectedTeam(team);
                                setShowWorkSpaceForm(true);
                              }}
                            >
                              {teamButtonData.label}
                            </MDButton>
                          }
                        >
                          <ListItemAvatar>
                            <Avatar
                              sx={{ bgcolor: GenerateRandomBackgroundColor() }}
                              variant="rounded"
                              src={team?.logo || null}
                            >
                              {team.name.charAt(0).toUpperCase()}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Typography
                                sx={{ display: "block", textTransform: "capitalize" }}
                                component="h5"
                                variant="body2"
                                color="text.primary"
                              >
                                {team?.name}
                              </Typography>
                            }
                            secondary={
                              <Typography
                                sx={{
                                  display: "inline",
                                  textTransform: "capitalize",
                                }}
                                component="span"
                                variant="body2"
                                color="text.secondary"
                              >
                                {team.memberCount || 0}
                                {(team.memberCount || 0) > 1 ? " members" : " member"}
                              </Typography>
                            }
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                </Card>
                <MDBox
                  display="flex"
                  mt={2}
                  mb={2}
                  sx={{ cursor: "pointer" }}
                  onClick={() => setShowWorkSpaceForm(true)}
                >
                  <Add sx={{ color: "#f83a46" }} />
                  <MDTypography variant="h6" color="primaryRed">
                    Create a new team
                  </MDTypography>
                </MDBox>

                {isLoading ? (
                  <Stack alignItems="center">
                    <CircularProgress
                      sx={{
                        position: "fixed",
                        left: "50%",
                        top: "50%",
                        transform: "translate(-50%, -50%)",
                        zIndex: 9,
                      }}
                      color="info"
                      size="10vh"
                    />
                  </Stack>
                ) : (
                  ""
                )}
              </MDBox>
            </MDBox>
          </CoverLayout>
        )
      )}
    </>
  );
}

export default Workspace;
